import { cn } from '@foam/buoy/src/lib/utils';
import { memo } from 'react';
import { match } from 'ts-pattern';
import { VideoHover } from '~/components/VideoHover';
import { ContentImage } from '~/screens/SearchContent/components/ContentImage';
import { ASSET_TYPE, ISearchContentResult } from '~/searchApi';
import { isSafariBrowser } from '~/utils-ts';

import { singleContentStyle } from '../styles';
import { getContentPreviewUrl } from '../utils';

interface ContentImageSectionProps {
  content: ISearchContentResult;
  interactive: boolean;
  isChecked?: boolean;
}

const ContentImageSectionComponent = ({
  content,
  interactive,
  isChecked = false,
}: ContentImageSectionProps) => {
  const previewUrl = getContentPreviewUrl(content);
  const isSafari = isSafariBrowser();

  return (
    <div
      className={cn(
        'flex h-[320px] w-full shrink-0 justify-center overflow-hidden rounded-t-12',
        {
          'group-hover:[&_img]:brightness-90 group-hover:[&_video]:brightness-90':
            interactive,
          '[&_img]:scale-[0.925] [&_video]:scale-[0.925] [&_[data-fallback-image]]:scale-[0.925] [&_img]:rounded-t-12 [&_video]:rounded-t-12 [&_[data-fallback-image]]:rounded-t-12':
            isChecked,
        },
      )}
      data-testid="content-image-section"
    >
      {match(content.type)
        .with(ASSET_TYPE.video, () => (
          <VideoHover
            src={content.assets.at(0)?.url ?? ''}
            poster={previewUrl}
            objectFit="cover"
            containerProps={{
              minHeight: singleContentStyle.root.height,
              className:
                'w-full flex justify-center object-cover align-bottom transition-all duration-100',
            }}
            className={cn(
              'h-full transition-transform duration-100 rounded-t-12',
              {
                'w-full': !isSafari,
              },
            )}
            fallback={
              <ContentImage
                draggable="false"
                minHeight={singleContentStyle.root.height}
                objectFit="cover"
                src={previewUrl}
                alt={content.caption}
                className={cn(
                  'object-cover align-bottom transition-all duration-100 h-full rounded-t-12',
                  {
                    'w-full': !isSafari,
                  },
                )}
                fallbackImageProps={{
                  minHeight: singleContentStyle.root.height,
                  height: '100%',
                  objectFit: 'cover',
                  paddingX: '4rem',
                  iconProps: { boxSize: 8 },
                  borderRadius: '0',
                }}
              />
            }
            hidePlayIcon
          />
        ))
        .otherwise(() => (
          <ContentImage
            draggable="false"
            height="100%"
            objectFit="cover"
            src={previewUrl}
            alt={content.caption}
            className={cn(
              'object-cover align-bottom transition-all duration-100 rounded-t-12',
              {
                'w-full': !isSafari,
              },
            )}
            fallbackImageProps={{
              height: '100%',
              objectFit: 'cover',
              paddingX: '4rem',
              iconProps: { boxSize: 8 },
            }}
          />
        ))}
    </div>
  );
};

export const ContentImageSection = memo(ContentImageSectionComponent);
